import * as Sentry from '@sentry/react';

const isDevelopment = import.meta.env.MODE !== 'production';
const MAX_SERIALIZATION_DEPTH = 4;
const MAX_ARRAY_ITEMS = 20;
const TRUNCATED_ARRAY_LABEL = '[Truncated Array]';
const TRUNCATED_OBJECT_LABEL = '[Truncated Object]';
const CIRCULAR_REFERENCE_LABEL = '[Circular]';
const FUNCTION_LABEL = '[Function]';
const SYMBOL_LABEL = '[Symbol]';
const BIGINT_SUFFIX = 'n';

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return Object.prototype.toString.call(value) === '[object Object]';
};

const serializeError = (
  error: Error,
  depth: number,
  seenObjects: WeakSet<object>
): Record<string, unknown> => {
  const serializedError: Record<string, unknown> = {
    name: error.name,
    message: error.message,
  };

  if (error.stack) {
    serializedError.stack = error.stack;
  }

  const errorWithCause = error as Error & { cause?: unknown };
  if (errorWithCause.cause !== undefined) {
    serializedError.cause = serializeForSentry(
      errorWithCause.cause,
      depth - 1,
      seenObjects
    );
  }

  Object.entries(error).forEach(([key, value]) => {
    if (!(key in serializedError)) {
      serializedError[key] = serializeForSentry(value, depth - 1, seenObjects);
    }
  });

  return serializedError;
};

export const serializeForSentry = (
  value: unknown,
  depth: number = MAX_SERIALIZATION_DEPTH,
  seenObjects: WeakSet<object> = new WeakSet<object>()
): unknown => {
  if (value === null || value === undefined) {
    return value;
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (typeof value === 'bigint') {
    return `${value.toString()}${BIGINT_SUFFIX}`;
  }

  if (typeof value === 'symbol') {
    return `${SYMBOL_LABEL}(${String(value.description ?? '')})`;
  }

  if (typeof value === 'function') {
    return `${FUNCTION_LABEL} ${value.name || 'anonymous'}`;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (depth <= 0) {
    if (Array.isArray(value)) {
      return TRUNCATED_ARRAY_LABEL;
    }

    return TRUNCATED_OBJECT_LABEL;
  }

  if (typeof value === 'object') {
    if (seenObjects.has(value)) {
      return CIRCULAR_REFERENCE_LABEL;
    }

    seenObjects.add(value);

    if (value instanceof Error) {
      return serializeError(value, depth, seenObjects);
    }

    if (Array.isArray(value)) {
      return value
        .slice(0, MAX_ARRAY_ITEMS)
        .map((item) => serializeForSentry(item, depth - 1, seenObjects));
    }

    if (isPlainObject(value)) {
      return Object.fromEntries(
        Object.entries(value).map(([key, nestedValue]) => [
          key,
          serializeForSentry(nestedValue, depth - 1, seenObjects),
        ])
      );
    }

    return String(value);
  }

  return String(value);
};

export const serializeRecordForSentry = (
  value: Record<string, unknown>
): Record<string, unknown> => {
  return Object.fromEntries(
    Object.entries(value).map(([key, entryValue]) => [
      key,
      serializeForSentry(entryValue),
    ])
  );
};

export const reportError = (error: Error, context?: Record<string, any>) => {
  if (isDevelopment) {
    console.error('Development Error:', error, context);
    return;
  }

  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext('additional_info', serializeRecordForSentry(context));
    }
    Sentry.captureException(error);
  });
};

export const reportMessage = (
  message: string,
  level: 'error' | 'warning' | 'info' = 'info'
) => {
  if (isDevelopment) {
    console[level === 'error' ? 'error' : level === 'warning' ? 'warn' : 'log'](
      'Development Message:',
      message
    );
    return;
  }

  Sentry.captureMessage(message, level);
};

export const setUserContext = (user: {
  id: string;
  email?: string;
  username?: string;
}) => {
  if (isDevelopment) {
    console.info('Development User Context:', user);
    return;
  }

  Sentry.setUser(user);
};

export const addBreadcrumb = (
  message: string,
  category?: string,
  level?: 'error' | 'warning' | 'info' | 'debug',
  data?: Record<string, unknown>
) => {
  if (isDevelopment) {
    return;
  }

  Sentry.addBreadcrumb({
    message,
    category: category || 'custom',
    data: data ? serializeRecordForSentry(data) : undefined,
    level: level || 'info',
    timestamp: Date.now() / 1000,
  });
};
